import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Shield, User as UserIcon } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminUsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password) {
      toast.error("Email and password are required");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/users", {
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim() || null,
        role: "user",
      });
      toast.success(`User ${form.email.trim()} created`);
      setOpen(false);
      setForm({ email: "", name: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id, email) => {
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success(`Deleted ${email}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete");
    }
  };

  return (
    <div className="px-8 py-7">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5">
            Admin
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-950">Users</h1>
          <p className="text-sm text-zinc-500 mt-1.5">
            Create sub-user accounts for your team. Sub-users share this product library.
          </p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              className="h-10 bg-zinc-950 text-white hover:bg-zinc-800 gap-2"
              data-testid="create-user-btn"
            >
              <UserPlus size={16} />
              New user
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md" data-testid="create-user-dialog">
            <DialogHeader>
              <DialogTitle>Create a sub-user</DialogTitle>
              <DialogDescription>
                They&apos;ll be able to log in with these credentials and access the shared product library.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-[12px] font-medium">Email *</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="h-10"
                  required
                  data-testid="new-user-email"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[12px] font-medium">Name (optional)</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="h-10"
                  data-testid="new-user-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[12px] font-medium">Password *</Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  minLength={6}
                  className="h-10"
                  required
                  data-testid="new-user-password"
                />
                <div className="text-[10px] text-zinc-500">
                  Minimum 6 characters. Share this with the sub-user via a secure channel.
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setOpen(false)}
                  className="h-10"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={creating}
                  className="h-10 bg-zinc-950 text-white hover:bg-zinc-800"
                  data-testid="submit-new-user-btn"
                >
                  {creating ? "Creating…" : "Create user"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid="users-list">
          <div className="grid grid-cols-[auto_minmax(0,2fr)_minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_auto] gap-4 px-5 py-3 border-b border-zinc-200 bg-zinc-50 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">
            <div>Role</div>
            <div>Email</div>
            <div>Name</div>
            <div>Created</div>
            <div />
            <div className="text-right">Actions</div>
          </div>
          {users.map((u) => (
            <div
              key={u.id}
              className="grid grid-cols-[auto_minmax(0,2fr)_minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_auto] gap-4 px-5 py-3.5 border-b border-zinc-100 last:border-b-0 items-center text-[13px]"
              data-testid={`user-row-${u.id}`}
            >
              <div>
                {u.role === "admin" ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-900 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                    <Shield size={11} />
                    Admin
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-zinc-700 bg-zinc-100 border border-zinc-200 rounded-full px-2 py-0.5">
                    <UserIcon size={11} />
                    User
                  </span>
                )}
              </div>
              <div className="text-zinc-950 font-medium truncate">{u.email}</div>
              <div className="text-zinc-700 truncate">{u.name || "—"}</div>
              <div className="text-zinc-500 text-[12px]">
                {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
              </div>
              <div />
              <div className="text-right">
                {u.role !== "admin" && u.id !== user?.id && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 px-2 hover:bg-red-50 hover:text-red-700"
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this user?</AlertDialogTitle>
                        <AlertDialogDescription>
                          <strong>{u.email}</strong> will lose access. Their products stay in the shared library.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-red-700 hover:bg-red-800"
                          onClick={() => remove(u.id, u.email)}
                          data-testid={`confirm-delete-user-${u.id}`}
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
